import { t } from 'i18next'
import React, { FC, useContext } from 'react'

import { AuthSessionContext } from '@context/AuthContext'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import { formatDate } from '@utils/date.utils'

import { CorporateActionDto } from '@model/CorporateActionDto'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import styles from './StockDividendsPage.module.scss'

type Props = {
  data: CorporateActionDto[]
  isLoading?: boolean
  sticky?: 'left'
}

export const StockDividendsTable: FC<Props> = ({ data, isLoading = false, sticky }) => {
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { formatMoney } = useFormatNumber()

  const noResults = !data?.length

  const headers: { name: string; align?: 'center' | 'right' }[] = [
    { name: t('Stock Dividends.Name') },
    { name: t('Stock Dividends.Symbol'), align: 'center' },
    { name: t('Stock Dividends.Dividend Ex Date'), align: 'center' },
    { name: t('Stock Dividends.Gross Dividend Amount'), align: 'center' },
  ]

  return (
    <>
      {noResults && !isLoading ? (
        <TableNoResults title={t('No results')} />
      ) : (
        <Table sticky={sticky} isLoading={isLoading} noResults={noResults}>
          <TableHeader>
            <TableRow>
              {headers.map(({ name, align }, idx) => (
                <TableHead key={`${name}_${idx}`} align={align}>
                  {name}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.map((x) => (
              <TableRow key={x.id}>
                <TableCell width={260}>{x.instrument.name ?? ''}</TableCell>

                <TableCell width={140} align='center'>
                  <div className={styles.symbolTag}>{x.instrument.symbol ?? ''}</div>
                </TableCell>

                <TableCell width={200} align={'center'}>
                  {x.date ? formatDate(x.date, { formatType: dateFormat }) : ''}
                </TableCell>

                <TableCell width={220} align={'center'}>
                  {formatMoney(
                    x.corporateActionDividend?.rate ?? 0,
                    (x.corporateActionDividend?.currencyId ?? 'USD') as CurrencyType
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  )
}
